import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { API_BASE_URL } from '../config';
import { useAuth } from '../contexts/AuthContext';

const staticFacetLabels = {
  programName: 'Program',
  modelStatus: 'Model Status',
  outdoorBrandName: 'Outdoor Brand',
  indoorBrandName: 'Indoor Brand',
  seriesName: 'Series',
  manufacturerType: 'Manufacturer',
  ahriType: 'AHRI Type',
  region: 'Region',
  refrigerantType: 'Refrigerant',
};

// Additional help text for System Builder facets so customers understand each filter.
const builderFacetHelpText = {
  rows_programname: 'Program type for this AHRI system, such as AC, ASHP, Furnace, etc.',
  rows_outdoorbrandname: 'Brand of the outdoor unit (condenser/heat pump) in the system.',
  rows_indoorbrandname: 'Brand of the indoor unit (air handler/furnace/coil) in the system.',
  rows_region: 'Geographic efficiency region the rating applies to (e.g. North, Southeast).',
  rows_seriesname: 'Manufacturer series or family name for this system.',
  rows_modelstatus: 'AHRI model status such as Active, Discontinued, or Obsolete.',
};

// Primary AHRI facets we want to highlight at the top of the facet list.
// These correspond to dynamic Solr fields built from row_json:
//   programName           -> rows_programname
//   outdoorBrandName      -> rows_outdoorbrandname
//   indoorBrandName       -> rows_indoorbrandname
//   ahri_complete_match   -> rows_ahri_complete_match
const PRIMARY_FACET_FIELDS = [
  'rows_programname',
  'rows_outdoorbrandname',
  'rows_indoorbrandname',
  'rows_ahri_complete_match',
];

const formatDetailValue = (value) => {
  if (value === null || value === undefined || value === '') {
    return '—';
  }
  if (Array.isArray(value)) {
    return value.map((item) => (typeof item === 'object' ? JSON.stringify(item) : String(item))).join(', ');
  }
  if (typeof value === 'object') {
    try {
      return JSON.stringify(value, null, 2);
    } catch (err) {
      return String(value);
    }
  }
  return String(value);
};

const renderDetailTable = (data) => {
  if (!data || typeof data !== 'object') {
    return <div className="text-muted small">No additional details available.</div>;
  }
  const entries = Object.entries(data).sort(([a], [b]) => a.localeCompare(b));
  return (
    <div className="table-responsive mt-2">
      <table className="table table-sm table-striped align-middle mb-0">
        <tbody>
          {entries.map(([key, value]) => (
            <tr key={key}>
              <th style={{ width: '30%' }} className="text-uppercase text-muted small">{key}</th>
              <td>
                <pre className="mb-0 text-break" style={{ whiteSpace: 'pre-wrap' }}>
                  {formatDetailValue(value)}
                </pre>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const prettyNumber = (num) => {
  if (num === undefined || num === null) return '—';
  return num.toLocaleString();
};

const makeFacetInputId = (prefix, value) => {
  const slug = String(value ?? 'unknown')
    .trim()
    .replace(/[^a-zA-Z0-9]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
    .toLowerCase();
  return `${prefix}-${slug || 'unknown'}`;
};

const humanizeFacetLabel = (field = '') => {
  if (!field) return 'Facet';
  const withoutPrefix = field.startsWith('rows_') ? field.replace('rows_', '') : field;
  const cleaned = withoutPrefix.replace(/_/g, ' ').trim();
  if (!cleaned) return field;
  return cleaned.replace(/\b\w/g, (c) => c.toUpperCase());
};

const ACTIVE_IMPORT_STATUSES = ['queued', 'running'];

const buildProductImageUrl = (raw, base) => {
  const src = (raw || '').trim();
  if (!src) return '';
  // If already an absolute URL (http/https or protocol-relative), use as-is.
  if (/^https?:\/\//i.test(src) || src.startsWith('//')) {
    return src;
  }
  const b = (base || '').trim();
  if (!b) return src;
  const needsSlash = !b.endsWith('/');
  return `${b}${needsSlash ? '/' : ''}${src}`;
};

const PRODUCT_PAGE_SIZE = 20;

// Small helper to render mapped product cards for an AHRI document
// using product codes stored in AHRI product_mapping (via prodmap_* fields).
const MappedProducts = ({ codes, headers, imageBaseUrl }) => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const uniqueCodes = useMemo(
    () => Array.from(new Set((codes || []).map((c) => String(c || '').trim()).filter(Boolean))),
    [codes],
  );

  useEffect(() => {
    if (!uniqueCodes.length) {
      setProducts([]);
      setError('');
      return undefined;
    }
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const resp = await fetch(`${API_BASE_URL}/api/v1/products/solr/search`, {
          method: 'POST',
          headers,
          body: JSON.stringify({
            query: '',
            partnumber: '',
            filters: { productCode: uniqueCodes },
            page: 1,
            page_size: Math.min(Math.max(uniqueCodes.length, 1), 100),
          }),
        });
        const data = await resp.json().catch(() => ({}));
        if (!resp.ok) {
          throw new Error(data?.detail || `Unable to load mapped products (HTTP ${resp.status})`);
        }
        const docs = Array.isArray(data.docs) ? data.docs : [];
        const byCode = new Map();
        docs.forEach((d) => {
          const code = String(d.productCode || d.product_code || '').trim();
          if (code) {
            byCode.set(code.toLowerCase(), d);
          }
        });
        const next = uniqueCodes.map((code) => {
          const found = byCode.get(String(code).trim().toLowerCase());
          if (found) {
            return { code, ...found };
          }
          return { code };
        });
        if (!cancelled) {
          setProducts(next);
          setError('');
        }
      } catch (e) {
        if (!cancelled) {
          setError(String(e?.message || e || 'Unable to load mapped products'));
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [headers, uniqueCodes]);

  if (!uniqueCodes.length) {
    return null;
  }

  return (
    <div className="mt-2">
      {loading && (
        <div className="small text-muted d-flex align-items-center gap-2">
          <span className="spinner-border spinner-border-sm text-primary" role="status" />
          <span>Loading mapped products…</span>
        </div>
      )}
      {error && !loading && (
        <div className="alert alert-warning py-1 px-2 small mb-2">
          {error}
        </div>
      )}
      {products.length > 0 && (
        <div className="row g-2">
          {products.map((p) => (
            <div key={p.code || p.product_code || p.id} className="col-12 col-sm-6 col-lg-4">
              <div className="border rounded-3 h-100 p-2 d-flex flex-column">
                <div className="d-flex align-items-center gap-2 mb-2">
                  <div
                    className="ratio ratio-1x1 rounded border bg-light flex-shrink-0"
                    style={{ width: 48, overflow: 'hidden' }}
                  >
                    {buildProductImageUrl(p.image_url || p.imageUrl, imageBaseUrl) ? (
                      // eslint-disable-next-line jsx-a11y/img-redundant-alt
                      <img
                        src={buildProductImageUrl(p.image_url || p.imageUrl, imageBaseUrl)}
                        alt={p.name || p.productCode || p.product_code || p.code || 'Product image'}
                        style={{ objectFit: 'cover' }}
                      />
                    ) : (
                      <div className="d-flex align-items-center justify-content-center text-muted small">
                        No image
                      </div>
                    )}
                  </div>
                  <div className="flex-grow-1">
                    <div className="fw-semibold small text-truncate">
                      {p.name || p.productCode || p.product_code || p.code || 'Mapped product'}
                    </div>
                    <div className="text-muted small text-truncate">
                      Code: <code>{p.productCode || p.product_code || p.code}</code>
                    </div>
                  </div>
                </div>
                {(p.manufacturer_name || p.manufacturerName) && (
                  <div className="text-muted small mb-1">
                    Manufacturer: {p.manufacturer_name || p.manufacturerName}
                  </div>
                )}
                {(p.manufacturer_part_number || p.manufacturerPartNumber) && (
                  <div className="text-muted small mb-1">
                    MPN: <code>{p.manufacturer_part_number || p.manufacturerPartNumber}</code>
                  </div>
                )}
                {p.description && (
                  <div className="text-muted small mb-1 text-truncate" title={p.description}>
                    {p.description}
                  </div>
                )}
                {p.category && (
                  <div className="mt-auto small">
                    <span className="badge bg-light text-dark">{p.category}</span>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
      {!loading && products.length === 0 && (
        <div className="small text-muted">No mapped products could be loaded.</div>
      )}
    </div>
  );
};

const AhriSolr = () => {
  const { getAuthHeaders } = useAuth();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [facets, setFacets] = useState({});
  const [filters, setFilters] = useState({});
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState('');
  const [stats, setStats] = useState({ numFound: 0, page: 1, page_size: 20 });
  const [collection, setCollection] = useState('');
  const [solrState, setSolrState] = useState({});
  const [importState, setImportState] = useState(null);
  const importStateRef = useRef(null);
  const queryRef = useRef(query);
  const filtersRef = useRef(filters);
  const [facetDefs, setFacetDefs] = useState([]);
  const isImportActive = importState ? ACTIVE_IMPORT_STATUSES.includes(importState.status) : false;
  const importProgress = useMemo(() => {
    if (!importState || !importState.total) return 0;
    return Math.min(100, Math.round(((importState.processed || 0) / importState.total) * 100));
  }, [importState]);
  const docCounts = solrState?.doc_counts || {};
  const totalDocs = docCounts.total_docs ?? null;
  const accountDocs = docCounts.account_docs ?? null;
  const orphanDocs = docCounts.orphan_docs ?? 0;
  const hasOrphans = Boolean(orphanDocs && orphanDocs > 0);
  const [activeTab, setActiveTab] = useState('ahri');
  const [productQuery, setProductQuery] = useState('');
  const [productPartnumber, setProductPartnumber] = useState('');
  const [productResults, setProductResults] = useState([]);
  const [productFacets, setProductFacets] = useState({});
  const [productFilters, setProductFilters] = useState({});
  const [productLoading, setProductLoading] = useState(false);
  const [productImporting, setProductImporting] = useState('');
  const [productImportState, setProductImportState] = useState(null);
  const productImportStateRef = useRef(null);
  const [productCollection, setProductCollection] = useState('');
  const [productStats, setProductStats] = useState({ numFound: 0, page: 1, page_size: PRODUCT_PAGE_SIZE });
  const productDocCounts = productImportState?.doc_counts || {};
  const productImportActive = productImportState ? ACTIVE_IMPORT_STATUSES.includes(productImportState.status) : false;
  const productQueryRef = useRef(productQuery);
  const productPartnumberRef = useRef(productPartnumber);
  const productFiltersRef = useRef(productFilters);
  const [expandedAhriDetails, setExpandedAhriDetails] = useState({});
  const [expandedProductDetails, setExpandedProductDetails] = useState({});
  const [productMatchSummaries, setProductMatchSummaries] = useState({});
  const [productMatchLoading, setProductMatchLoading] = useState({});
  const [productMatchErrors, setProductMatchErrors] = useState({});
  const [productMatchFilters, setProductMatchFilters] = useState({});
  const [productMatchFacetFilters, setProductMatchFacetFilters] = useState({});
  // Per-product pre-fetched mapped product details for AHRI match
  // Shape: { [docKey]: { loading: bool, error: string, byCode: { [codeLower]: productDoc } } }
  const [productMatchProducts, setProductMatchProducts] = useState({});
  // System Builder (AC system configuration) state
  const [builderQuery, setBuilderQuery] = useState('');
  const [builderResults, setBuilderResults] = useState([]);
  const [builderLoading, setBuilderLoading] = useState(false);
  const [builderStats, setBuilderStats] = useState({ numFound: 0, page: 1, page_size: 12 });
  const [builderFilters, setBuilderFilters] = useState({});
  const [selectedSystemKey, setSelectedSystemKey] = useState(null);
  const [builderFacets, setBuilderFacets] = useState({});
  const [builderFacetDefs, setBuilderFacetDefs] = useState([]);
  const [expandedBuilderAttributes, setExpandedBuilderAttributes] = useState({});
  const [productImageBaseUrl, setProductImageBaseUrl] = useState('https://www.gearypacific.com//ASSETS/IMAGES/ITEMS/ZOOM/');

  const headers = useMemo(() => ({
    'Content-Type': 'application/json',
    ...getAuthHeaders(),
  }), [getAuthHeaders]);

  useEffect(() => {
    const loadImageBase = async () => {
      try {
        const resp = await fetch(`${API_BASE_URL}/api/v1/ahri/config`, { headers });
        const data = await resp.json().catch(() => ({}));
        const url = data?.config?.product_image_fallback_base_url;
        if (typeof url === 'string' && url.trim()) {
          setProductImageBaseUrl(url.trim());
        }
      } catch {
        // ignore config load errors; keep default
      }
    };
    loadImageBase();
  }, [headers]);

  const toggleAhriDetail = useCallback((key) => {
    setExpandedAhriDetails((prev) => ({
      ...prev,
      [key]: !prev?.[key],
    }));
  }, []);

  const toggleProductDetail = useCallback((key) => {
    setExpandedProductDetails((prev) => ({
      ...prev,
      [key]: !prev?.[key],
    }));
  }, []);

  const loadAhriMatchForProduct = useCallback(async (docKey, productCode) => {
    if (!productCode) return;
    setProductMatchLoading((prev) => ({ ...prev, [docKey]: true }));
    setProductMatchErrors((prev) => ({ ...prev, [docKey]: '' }));
    setProductMatchProducts((prev) => ({
      ...prev,
      [docKey]: { ...(prev[docKey] || {}), loading: true, error: '', byCode: prev[docKey]?.byCode || {} },
    }));
    try {
      const resp = await fetch(`${API_BASE_URL}/api/v1/ahri/product-match-summary`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ product_code: productCode, field: 'indoorModelNumber' }),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        const msg = data?.detail || `Unable to load AHRI match (HTTP ${resp.status})`;
        setProductMatchErrors((prev) => ({ ...prev, [docKey]: msg }));
      } else {
        setProductMatchSummaries((prev) => ({ ...prev, [docKey]: data }));
        // After we know all AHRI matches, batch-resolve mapped products from Solr in a single request.
        const matches = Array.isArray(data.matches) ? data.matches : [];
        const codesSet = new Set();
        matches.forEach((m) => {
          const mapping = m.product_mapping || {};
          Object.values(mapping).forEach((rawVal) => {
            const vals = Array.isArray(rawVal)
              ? rawVal
              : String(rawVal || '')
                  .split(',')
                  .map((s) => s.trim());
            vals
              .map((v) => String(v || '').trim())
              .filter(Boolean)
              .forEach((v) => {
                const lower = v.toLowerCase();
                // Exclude the current product itself from mapped products
                if (String(productCode || '').trim().toLowerCase() === lower) return;
                codesSet.add(v);
              });
          });
        });
        const uniqueCodes = Array.from(codesSet);
        if (uniqueCodes.length > 0) {
          try {
            const solrResp = await fetch(`${API_BASE_URL}/api/v1/products/solr/search`, {
              method: 'POST',
              headers,
              body: JSON.stringify({
                query: '',
                partnumber: '',
                filters: { productCode: uniqueCodes },
                page: 1,
                page_size: Math.min(Math.max(uniqueCodes.length, 1), 200),
              }),
            });
            const solrData = await solrResp.json().catch(() => ({}));
            if (!solrResp.ok) {
              const msg = solrData?.detail || `Unable to load mapped products (HTTP ${solrResp.status})`;
              setProductMatchProducts((prev) => ({
                ...prev,
                [docKey]: { ...(prev[docKey] || {}), loading: false, error: msg, byCode: prev[docKey]?.byCode || {} },
              }));
            } else {
              const docs = Array.isArray(solrData.docs) ? solrData.docs : [];
              const byCode = {};
              docs.forEach((d) => {
                const code = String(d.productCode || d.product_code || '').trim();
                if (code) {
                  byCode[code.toLowerCase()] = d;
                }
              });
              setProductMatchProducts((prev) => ({
                ...prev,
                [docKey]: { ...(prev[docKey] || {}), loading: false, error: '', byCode },
              }));
            }
          } catch (e) {
            setProductMatchProducts((prev) => ({
              ...prev,
              [docKey]: {
                ...(prev[docKey] || {}),
                loading: false,
                error: String(e?.message || e || 'Unable to load mapped products'),
                byCode: prev[docKey]?.byCode || {},
              },
            }));
          }
        } else {
          setProductMatchProducts((prev) => ({
            ...prev,
            [docKey]: { ...(prev[docKey] || {}), loading: false, error: '', byCode: prev[docKey]?.byCode || {} },
          }));
        }
      }
    } catch (err) {
      setProductMatchErrors((prev) => ({ ...prev, [docKey]: String(err) }));
      setProductMatchProducts((prev) => ({
        ...prev,
        [docKey]: {
          ...(prev[docKey] || {}),
          loading: false,
          error: prev[docKey]?.error || '',
          byCode: prev[docKey]?.byCode || {},
        },
      }));
    } finally {
      setProductMatchLoading((prev) => ({ ...prev, [docKey]: false }));
    }
  }, [headers]);

  const runSearch = useCallback(async (page = 1, nextFilters, nextQuery) => {
    const filtersToUse = nextFilters ?? filtersRef.current;
    const queryToUse = nextQuery ?? queryRef.current;
    setLoading(true);
    try {
      const resp = await fetch(`${API_BASE_URL}/api/v1/ahri/solr/search`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          query: queryToUse,
          page,
          page_size: 20,
          filters: filtersToUse,
        }),
      });
      const data = await resp.json();
      if (!resp.ok) {
        throw new Error(data?.detail || 'Solr search failed');
      }
      setResults(data.docs || []);
      setFacets(data.facets || {});
      setFacetDefs(data.facet_definitions || []);
      const appliedFilters = data.filters || filtersToUse || {};
      setFilters(appliedFilters);
      setStats({ numFound: data.numFound || 0, page: data.page || 1, page_size: data.page_size || 20 });
      setCollection(data.collection || '');
      const mergedState = { ...(data.state || {}) };
      if (data.doc_counts) {
        mergedState.doc_counts = data.doc_counts;
      }
      setSolrState(mergedState);
    } catch (err) {
      toast.error(err.message || 'Unable to fetch Solr data');
    } finally {
      setLoading(false);
    }
  }, [headers]);

  const runProductSearch = useCallback(async (options = {}) => {
    const nextFilters = options.filters ?? productFiltersRef.current;
    const nextQuery = options.query ?? productQueryRef.current;
    const nextPartnumber = options.partnumber ?? productPartnumberRef.current;
    const nextPage = Math.max(1, options.page || 1);
    setProductLoading(true);
    try {
      const resp = await fetch(`${API_BASE_URL}/api/v1/products/solr/search`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          query: nextQuery,
          partnumber: nextPartnumber,
          filters: nextFilters,
          page: nextPage,
          page_size: PRODUCT_PAGE_SIZE,
        }),
      });
      const data = await resp.json();
      if (!resp.ok) {
        throw new Error(data?.detail || 'Product search failed');
      }
      setProductResults(data.docs || []);
      setProductFacets(data.facets || {});
      setProductFilters(data.filters || nextFilters || {});
      setProductCollection(data.collection || '');
      setProductStats({
        numFound: data.numFound || 0,
        page: data.page || nextPage,
        page_size: data.page_size || PRODUCT_PAGE_SIZE,
      });
      setProductImportState((prev) => ({
        ...(prev || {}),
        ...(data.state || {}),
        doc_counts: data.doc_counts || prev?.doc_counts,
      }));
    } catch (err) {
      toast.error(err.message || 'Unable to run product search');
    } finally {
      setProductLoading(false);
    }
  }, [headers]);

  const loadImportState = useCallback(async () => {
    try {
      const resp = await fetch(`${API_BASE_URL}/api/v1/ahri/solr/import/state`, { headers });
      const data = await resp.json();
      if (resp.ok) {
        const prevState = importStateRef.current;
        const nextState = data.state || null;
        setImportState(nextState);
        if (nextState) {
          setSolrState((prev) => ({ ...prev, ...nextState }));
        }
        const prevStatus = prevState?.status;
        const nextStatus = nextState?.status;
        const sameJob = prevState?.import_id && nextState?.import_id && prevState.import_id === nextState.import_id;
        if (prevState && sameJob && prevStatus !== 'completed' && nextStatus === 'completed') {
          runSearch();
        }
      }
    } catch (err) {
      console.error('Failed to load import state', err);
    }
  }, [headers, runSearch]);

  const loadProductImportState = useCallback(async () => {
    try {
      const resp = await fetch(`${API_BASE_URL}/api/v1/products/solr/import/state`, { headers });
      const data = await resp.json();
      if (resp.ok) {
        const prev = productImportStateRef.current;
        const next = data.state || null;
        setProductImportState(next);
        const prevJob = prev?.import_id;
        const nextJob = next?.import_id;
        if (prev && next && prevJob && nextJob && prevJob === nextJob && prev.status !== 'completed' && next.status === 'completed') {
          runProductSearch();
        }
      }
    } catch (err) {
      console.error('Failed to load product import state', err);
    }
  }, [headers, runProductSearch]);

  useEffect(() => {
    runSearch();
    loadImportState();
    runProductSearch();
    loadProductImportState();
  }, [loadImportState, loadProductImportState, runProductSearch, runSearch]);

  useEffect(() => {
    importStateRef.current = importState;
  }, [importState]);

  useEffect(() => {
    productImportStateRef.current = productImportState;
  }, [productImportState]);

  useEffect(() => {
    queryRef.current = query;
  }, [query]);

  useEffect(() => {
    filtersRef.current = filters;
  }, [filters]);

  useEffect(() => {
    productQueryRef.current = productQuery;
  }, [productQuery]);

  useEffect(() => {
    productPartnumberRef.current = productPartnumber;
  }, [productPartnumber]);

  useEffect(() => {
    productFiltersRef.current = productFilters;
  }, [productFilters]);

  useEffect(() => {
    if (!isImportActive) {
      return undefined;
    }
    // Refresh immediately to capture state transitions quickly
    loadImportState();
    const id = setInterval(() => {
      loadImportState();
    }, 3000);
    return () => clearInterval(id);
  }, [isImportActive, loadImportState]);

  useEffect(() => {
    if (!productImportActive) {
      return undefined;
    }
    loadProductImportState();
    const id = setInterval(() => {
      loadProductImportState();
    }, 3000);
    return () => clearInterval(id);
  }, [loadProductImportState, productImportActive]);

  const toggleFacetValue = (field, value) => {
    const current = filters[field] || [];
    let nextValues;
    if (current.includes(value)) {
      nextValues = current.filter((v) => v !== value);
    } else {
      nextValues = [...current, value];
    }
    const nextFilters = { ...filters, [field]: nextValues };
    if (nextValues.length === 0) {
      delete nextFilters[field];
    }
    setFilters(nextFilters);
    runSearch(1, nextFilters, query);
  };

  const clearFilters = () => {
    setFilters({});
    runSearch(1, {}, queryRef.current);
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    runSearch(1, filters, query);
  };

  const triggerImport = async (mode) => {
    setImporting(mode);
    try {
      const resp = await fetch(`${API_BASE_URL}/api/v1/ahri/solr/import`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ mode }),
      });
      const data = await resp.json();
      if (!resp.ok) {
        throw new Error(data?.detail || 'Solr import failed');
      }
      toast.success(`Solr ${mode} import started`);
      loadImportState();
    } catch (err) {
      toast.error(err.message || `Unable to run ${mode} import`);
    } finally {
      setImporting('');
    }
  };

  const triggerProductImport = async (mode) => {
    setProductImporting(mode);
    try {
      const resp = await fetch(`${API_BASE_URL}/api/v1/products/solr/import`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ mode }),
      });
      const data = await resp.json();
      if (!resp.ok) {
        throw new Error(data?.detail || 'Product Solr import failed');
      }
      toast.success(`Product Solr ${mode} import started`);
      loadProductImportState();
    } catch (err) {
      toast.error(err.message || `Unable to run ${mode} product import`);
    } finally {
      setProductImporting('');
    }
  };

  const handleProductSearchSubmit = (e) => {
    e.preventDefault();
    runProductSearch({ query: productQuery, partnumber: productPartnumber });
  };

  const toggleProductFacetValue = (field, value) => {
    const current = productFilters[field] || [];
    let nextValues;
    if (current.includes(value)) {
      nextValues = current.filter((v) => v !== value);
    } else {
      nextValues = [...current, value];
    }
    const nextFilters = { ...productFilters, [field]: nextValues };
    if (nextValues.length === 0) {
      delete nextFilters[field];
    }
    setProductFilters(nextFilters);
    runProductSearch({ filters: nextFilters });
  };

  const setProductActiveFilter = (value) => {
    const nextFilters = { ...productFilters };
    if (value === '') {
      delete nextFilters.active;
    } else {
      nextFilters.active = value;
    }
    setProductFilters(nextFilters);
    runProductSearch({ filters: nextFilters });
  };

  const clearProductFilters = () => {
    setProductFilters({});
    runProductSearch({ filters: {} });
  };

  const runBuilderSearch = useCallback(
    async (options = {}) => {
      const nextPage = Math.max(1, options.page || 1);
      const nextQuery = typeof options.query === 'string' ? options.query : builderQuery;
      const baseFilters = { ...(options.filters || builderFilters || {}) };
      setBuilderLoading(true);
      try {
        const resp = await fetch(`${API_BASE_URL}/api/v1/ahri/solr/search`, {
          method: 'POST',
          headers,
          body: JSON.stringify({
            query: nextQuery,
            page: nextPage,
            page_size: 12,
            filters: baseFilters,
            context: 'system_builder',
          }),
        });
        const data = await resp.json().catch(() => ({}));
        if (!resp.ok) {
          throw new Error(data?.detail || 'System builder search failed');
        }
        setBuilderResults(Array.isArray(data.docs) ? data.docs : []);
        setBuilderStats({
          numFound: data.numFound || 0,
          page: data.page || nextPage,
          page_size: data.page_size || 12,
        });
        setBuilderFilters(data.filters || baseFilters || {});
        setBuilderFacets(data.facets || {});
        setBuilderFacetDefs(data.facet_definitions || []);
      } catch (err) {
        toast.error(err.message || 'Unable to fetch systems');
      } finally {
        setBuilderLoading(false);
      }
    },
    [builderFilters, builderQuery, headers],
  );

  const handleBuilderSearchSubmit = (e) => {
    e.preventDefault();
    runBuilderSearch({ page: 1, query: builderQuery });
  };

  const toggleBuilderFacetValue = (field, value) => {
    const current = builderFilters[field] || [];
    let nextValues;
    if (current.includes(value)) {
      nextValues = current.filter((v) => v !== value);
    } else {
      nextValues = [...current, value];
    }
    const nextFilters = { ...builderFilters, [field]: nextValues };
    if (nextValues.length === 0) {
      delete nextFilters[field];
    }
    setBuilderFilters(nextFilters);
    runBuilderSearch({ page: 1, filters: nextFilters, query: builderQuery });
  };

  const clearBuilderFilters = () => {
    const base = {};
    setBuilderFilters(base);
    runBuilderSearch({ page: 1, filters: base, query: builderQuery });
  };

  const orderedFacetDefs = useMemo(() => {
    const base = facetDefs.length > 0
      ? facetDefs
      : Object.keys(facets).map((field) => ({
          field,
          label: staticFacetLabels[field] || humanizeFacetLabel(field),
          type: 'auto',
        }));
    const primarySet = new Set(PRIMARY_FACET_FIELDS);
    const primary = base.filter((fd) => primarySet.has(fd.field));
    const secondary = base.filter((fd) => !primarySet.has(fd.field));
    // Preserve explicit primary ordering from PRIMARY_FACET_FIELDS
    const orderedPrimary = PRIMARY_FACET_FIELDS
      .map((field) => primary.find((fd) => fd.field === field))
      .filter(Boolean);
    return [...orderedPrimary, ...secondary];
  }, [facetDefs, facets]);

  const orderedBuilderFacetDefs = useMemo(() => {
    const base =
      builderFacetDefs.length > 0
        ? builderFacetDefs
        : Object.keys(builderFacets).map((field) => ({
            field,
            label: staticFacetLabels[field] || humanizeFacetLabel(field),
            type: 'auto',
          }));
    // Hide the complete-match facet; it is always enforced
    return base.filter((fd) => fd.field !== 'rows_ahri_complete_match');
  }, [builderFacetDefs, builderFacets]);

  // Auto-load first page of systems and facets when System Builder tab is opened
  useEffect(() => {
    if (activeTab === 'builder' && !builderLoading && builderResults.length === 0) {
      runBuilderSearch({ page: 1, query: builderQuery });
    }
  }, [activeTab, builderLoading, builderResults.length, builderQuery, runBuilderSearch]);

  return (
    <div className="container-fluid">
      <ul className="nav nav-pills mb-4">
        <li className="nav-item">
          <button
            type="button"
            className={`nav-link ${activeTab === 'ahri' ? 'active' : ''}`}
            onClick={() => setActiveTab('ahri')}
          >
            AHRI Search
          </button>
        </li>
        <li className="nav-item">
          <button
            type="button"
            className={`nav-link ${activeTab === 'products' ? 'active' : ''}`}
            onClick={() => setActiveTab('products')}
          >
            Product Search
          </button>
        </li>
        <li className="nav-item">
          <button
            type="button"
            className={`nav-link ${activeTab === 'builder' ? 'active' : ''}`}
            onClick={() => setActiveTab('builder')}
          >
            System Builder
          </button>
        </li>
      </ul>

      {activeTab === 'ahri' && (
        <>
      <div className="bg-primary text-white rounded-4 p-4 mb-4">
        <div className="d-flex flex-column flex-lg-row align-items-start align-items-lg-center justify-content-between gap-3">
          <div>
            <h2 className="mb-1">Solr Search & Faceting</h2>
            <p className="mb-0 text-white-50">Explore indexed AHRI data with instant search, rich filters, and SolrCloud-backed performance.</p>
          </div>
          <div className="d-flex gap-2 flex-wrap">
            <button
              className="btn btn-light"
              disabled={importing === 'full' || isImportActive}
              onClick={() => triggerImport('full')}
            >
              {importing === 'full' ? 'Starting Full Import...' : isImportActive ? 'Import Running' : 'Run Full Import'}
            </button>
            <button
              className="btn btn-outline-light"
              disabled={importing === 'delta' || isImportActive}
              onClick={() => triggerImport('delta')}
            >
              {importing === 'delta' ? 'Starting Delta Import...' : isImportActive ? 'Import Running' : 'Run Delta Import'}
            </button>
          </div>
        </div>
        <div className="mt-3 d-flex flex-wrap gap-3 small text-white-50">
          <div>Collection: <span className="text-white">{collection || 'Not created yet'}</span></div>
          <div>Last Sync: <span className="text-white">{solrState?.last_synced_at ? new Date(solrState.last_synced_at).toLocaleString() : '—'}</span></div>
          <div>Mode: <span className="text-white">{solrState?.last_mode || '—'}</span></div>
          <div>Status: <span className="text-white">{importState?.status || 'idle'}</span></div>
          <div>Docs (acct / total): <span className="text-white">{prettyNumber(accountDocs ?? 0)} / {prettyNumber((totalDocs ?? accountDocs) ?? 0)}</span></div>
          <div>Orphan Docs: <span className={hasOrphans ? 'text-warning fw-semibold' : 'text-white'}>{prettyNumber(orphanDocs || 0)}</span></div>
        </div>
        {isImportActive && (
          <div className="mt-3">
            <div className="d-flex justify-content-between small text-white-50 mb-1">
              <span>Importing {importState?.mode} data</span>
              <span>{prettyNumber(importState?.processed || 0)} / {prettyNumber(importState?.total || 0)} ({importProgress}%)</span>
            </div>
            <div className="progress" style={{ height: '6px' }}>
              <div className="progress-bar bg-light" style={{ width: `${importProgress}%` }} />
            </div>
          </div>
        )}
        {!isImportActive && hasOrphans && (
          <div className="alert alert-warning mt-3 mb-0 py-2 px-3 small">
            {prettyNumber(orphanDocs || 0)} orphan Solr documents detected. A scheduled cleanup runs automatically, but you can trigger a full import to refresh immediately.
          </div>
        )}
      </div>

      <div className="row g-4">
        {/* Facets */}
        <div className="col-12 col-xl-3">
          <div className="card shadow-sm h-100">
            <div className="card-body">
              <div className="d-flex align-items-center justify-content-between mb-3">
                <h6 className="mb-0">Facets</h6>
                <button className="btn btn-link btn-sm" onClick={clearFilters} disabled={Object.keys(filters).length === 0}>
                  Clear
                </button>
              </div>
              {/* Primary (key) facets displayed as compact pills above the full facet list */}
              <div className="mb-3">
                <div className="text-muted text-uppercase small mb-1">Key Filters</div>
                <div className="d-flex flex-column gap-2">
                  {PRIMARY_FACET_FIELDS.map((field) => {
                    const facet = orderedFacetDefs.find((fd) => fd.field === field);
                    if (!facet) return null;
                    const values = (facets[field] || []).slice(0, 6);
                    if (!values.length) return null;
                    const label = facet.label || staticFacetLabels[field] || humanizeFacetLabel(field);
                    return (
                      <div key={field} className="d-flex flex-column gap-1">
                        <div className="small text-muted">{label}</div>
                        <div className="d-flex flex-wrap gap-1">
                          {values.map(({ value }) => {
                            const isActive = (filters[field] || []).includes(value);
                            const buttonLabel = value === true || value === 'true'
                              ? 'Yes'
                              : value === false || value === 'false'
                                ? 'No'
                                : (value || '—');
                            return (
                              <button
                                key={`${field}-${String(value)}`}
                                type="button"
                                className={`btn btn-sm rounded-pill ${
                                  isActive ? 'btn-primary' : 'btn-outline-secondary'
                                }`}
                                onClick={() => toggleFacetValue(field, value)}
                              >
                                {buttonLabel}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
              <div className="accordion" id="facetAccordion">
                {orderedFacetDefs.map((facet, idx) => {
                  const field = facet.field;
                  const values = facets[field] || [];
                  const label = facet.label || staticFacetLabels[field] || humanizeFacetLabel(field);
                  return (
                    <div className="accordion-item" key={field}>
                      <h2 className="accordion-header" id={`facet-${field}`}>
                        <button className={`accordion-button ${idx === 0 ? '' : 'collapsed'}`} type="button" data-bs-toggle="collapse" data-bs-target={`#facet-panel-${field}`} aria-expanded={idx === 0} aria-controls={`facet-panel-${field}`}>
                          {label}
                        </button>
                      </h2>
                      <div id={`facet-panel-${field}`} className={`accordion-collapse collapse ${idx === 0 ? 'show' : ''}`} data-bs-parent="#facetAccordion">
                        <div className="accordion-body p-0">
                          <ul className="list-group list-group-flush">
                            {values.map(({ value, count }) => (
                              <li key={value} className="list-group-item d-flex align-items-center justify-content-between">
                                <div className="form-check">
                                  <input
                                    className="form-check-input"
                                    type="checkbox"
                                    id={`${field}-${value}`}
                                    checked={(filters[field] || []).includes(value)}
                                    onChange={() => toggleFacetValue(field, value)}
                                  />
                                  <label className="form-check-label" htmlFor={`${field}-${value}`}>
                                    {value || <span className="text-muted">—</span>}
                                  </label>
                                </div>
                                <span className="badge bg-light text-dark">{count}</span>
                              </li>
                            ))}
                            {values.length === 0 && (
                              <li className="list-group-item text-muted small">No values</li>
                            )}
                          </ul>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        {/* Search + results */}
        <div className="col-12 col-xl-9">
          <div className="card shadow-sm mb-4">
            <div className="card-body">
              <form onSubmit={handleSearchSubmit} className="row g-3 align-items-end">
                <div className="col-md-9">
                  <label className="form-label text-muted text-uppercase small fw-semibold">Search</label>
                  <input
                    className="form-control form-control-lg"
                    placeholder="e.g., heat pump 2 ton, GoodMan series"
                    value={query}
                    onChange={(e)=>setQuery(e.target.value)}
                  />
                </div>
                <div className="col-md-3 d-grid d-md-flex align-items-end">
                  <button type="submit" className="btn btn-primary btn-lg w-100" disabled={loading}>
                    {loading ? 'Searching...' : 'Search'}
                  </button>
                </div>
              </form>
              <div className="mt-3 d-flex flex-column flex-md-row justify-content-between align-items-md-center text-muted small gap-2">
                <div>
                  Showing {results.length} of {prettyNumber(stats.numFound)} matches • Page {stats.page}
                </div>
                <div className="d-flex gap-2 mt-1 mt-md-0">
                  <button
                    type="button"
                    className="btn btn-outline-secondary btn-sm"
                    disabled={loading || stats.page <= 1}
                    onClick={() => runSearch(Math.max(1, (stats.page || 1) - 1))}
                  >
                    Previous
                  </button>
                  <button
                    type="button"
                    className="btn btn-outline-secondary btn-sm"
                    disabled={
                      loading ||
                      (stats.page || 1) >=
                        Math.max(
                          1,
                          Math.ceil(
                            (stats.numFound || 0) / (stats.page_size || 20),
                          ),
                        )
                    }
                    onClick={() => runSearch((stats.page || 1) + 1)}
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
          </div>

          {loading && (
            <div className="card shadow-sm mb-4">
              <div className="card-body text-center py-5">
                <div className="spinner-border text-primary mb-3" role="status" />
                <div className="text-muted">Loading Solr results...</div>
              </div>
            </div>
          )}

          {!loading && results.length === 0 && (
            <div className="card shadow-sm">
              <div className="card-body text-center py-5">
                <h5>No results yet</h5>
                <p className="text-muted mb-0">Try adjusting your search terms or facets.</p>
              </div>
            </div>
          )}

          {!loading && results.length > 0 && (
            <div className="row g-3">
              {results.map((doc, index) => {
                const docKey = doc.id || doc.referenceId || doc.ahriId || doc.ahriNumber || doc.productCode || `ahri-${index}`;
                let payloadObj = {};
                let prettyPayload = '';
                if (doc.payload_json) {
                  try {
                    payloadObj = JSON.parse(doc.payload_json);
                    prettyPayload = JSON.stringify(payloadObj, null, 2);
                  } catch (err) {
                    payloadObj = {};
                    prettyPayload = doc.payload_json;
                  }
                }
                const programName =
                  payloadObj.programName
                  || payloadObj.program
                  || (Array.isArray(doc.rows_programname) ? doc.rows_programname[0] : undefined)
                  || 'Program';
                const modelStatus =
                  payloadObj.modelStatus
                  || (Array.isArray(doc.rows_modelstatus) ? doc.rows_modelstatus[0] : undefined);
                const outdoorBrandName =
                  payloadObj.outdoorBrandName
                  || (Array.isArray(doc.rows_outdoorbrandname) ? doc.rows_outdoorbrandname[0] : undefined);
                const indoorBrandName =
                  payloadObj.indoorBrandName
                  || (Array.isArray(doc.rows_indoorbrandname) ? doc.rows_indoorbrandname[0] : undefined);
                // Prefer seriesName / series from payload, otherwise fall back to any
                // dynamic rows_series* field present on the Solr document.
                const seriesName = (() => {
                  if (payloadObj.seriesName) return payloadObj.seriesName;
                  if (payloadObj.series) return payloadObj.series;
                  const seriesKeys = Object.keys(doc || {}).filter((k) => k.startsWith('rows_series'));
                  for (const key of seriesKeys) {
                    const v = doc[key];
                    if (Array.isArray(v) && v.length > 0 && v[0]) return v[0];
                    if (typeof v === 'string' && v.trim()) return v;
                  }
                  return undefined;
                })();
                const region =
                  payloadObj.region
                  || (Array.isArray(doc.rows_region) ? doc.rows_region[0] : undefined);
                const seer =
                  payloadObj.seerM
                  ?? payloadObj.seerM1
                  ?? payloadObj.seer
                  ?? null;
                const eer95 =
                  payloadObj.eer95FM
                  ?? payloadObj.eer95FM1
                  ?? payloadObj.eer95
                  ?? null;
                const isEnergyStar =
                  payloadObj.isEnergyStar
                  ?? (Array.isArray(doc.rowb_isenergystar) ? doc.rowb_isenergystar[0] : undefined);
                const completeMatch = Boolean(payloadObj.ahri_complete_match);
                // Derive AHRI ID for display: prefer explicit ahri_id-like fields,
                // otherwise fall back to the canonical part of the Solr document id.
                let ahriId =
                  payloadObj.ahri_id
                  || payloadObj.ahriId
                  || payloadObj.referenceId
                  || payloadObj.AHRIReferenceNumber
                  || null;
                if (!ahriId && doc.id) {
                  const idParts = String(doc.id).split('_', 2);
                  ahriId = idParts.length === 2 ? idParts[1] : doc.id;
                }

                // Extract mapped product codes from dynamic prodmap_* fields
                const mappedProductCodes = Array.from(
                  new Set(
                    Object.entries(doc)
                      .filter(([key]) => key.startsWith('prodmap_'))
                      .flatMap(([, value]) => (Array.isArray(value) ? value : [value]))
                      .map((v) => String(v || '').trim())
                      .filter(Boolean),
                  ),
                );

                return (
                <div className="col-12" key={docKey}>
                  <div className="card border-0 shadow-sm h-100">
                    <div className="card-body">
                      <div className="d-flex flex-wrap justify-content-between align-items-start gap-2">
                        <div>
                          <h5 className="mb-1">{programName}</h5>
                          <div className="text-muted small">
                            AHRI ID: <code>{ahriId || 'n/a'}</code>
                          </div>
                        </div>
                        <div className="d-flex flex-wrap gap-2">
                          {modelStatus && <span className="badge bg-primary-subtle text-primary">{modelStatus}</span>}
                          {outdoorBrandName && <span className="badge bg-secondary-subtle text-secondary">{outdoorBrandName}</span>}
                          {indoorBrandName && <span className="badge bg-info-subtle text-info">{indoorBrandName}</span>}
                          {isEnergyStar && <span className="badge bg-success">Energy Star</span>}
                          {completeMatch && (
                            <span className="badge bg-success-subtle text-success">
                              Complete Match
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="row mt-3 g-3">
                        <div className="col-md-3">
                          <div className="text-muted small">Series</div>
                          <div className="fw-semibold">{seriesName || '—'}</div>
                        </div>
                        <div className="col-md-3">
                          <div className="text-muted small">SEER</div>
                          <div className="fw-semibold">{seer ?? '—'}</div>
                        </div>
                        <div className="col-md-3">
                          <div className="text-muted small">EER95</div>
                          <div className="fw-semibold">{eer95 ?? '—'}</div>
                        </div>
                        <div className="col-md-3">
                          <div className="text-muted small">Region</div>
                          <div className="fw-semibold">{region || '—'}</div>
                        </div>
                      </div>
                      {mappedProductCodes.length > 0 && (
                        <div className="mt-3">
                          <div className="d-flex justify-content-between align-items-center mb-1">
                            <h6 className="mb-0 small text-uppercase text-muted">Mapped Product Codes</h6>
                            <div className="small text-muted">
                              {mappedProductCodes.length} code{mappedProductCodes.length === 1 ? '' : 's'}
                            </div>
                          </div>
                          <div className="small text-muted text-break">
                            {mappedProductCodes.map((code) => (
                              <code key={code} className="me-1 d-inline-block">
                                {code}
                              </code>
                            ))}
                          </div>
                        </div>
                      )}
                      {doc.payload_json && (
                        <details className="mt-3">
                          <summary className="text-primary">View payload JSON</summary>
                          <pre className="bg-light rounded p-3 mt-2 small" style={{ maxHeight: 260, overflow: 'auto' }}>
                            {prettyPayload}
                          </pre>
                        </details>
                      )}
                      <div className="mt-3">
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-secondary"
                          onClick={() => toggleAhriDetail(docKey)}
                        >
                          {expandedAhriDetails[docKey] ? 'Hide Info' : 'More Info'}
                        </button>
                        {expandedAhriDetails[docKey] && renderDetailTable(doc)}
                      </div>
                    </div>
                  </div>
                </div>
              )})}
            </div>
          )}
        </div>
      </div>
        </>
      )}

      {activeTab === 'products' && (
      <div className="row g-4">
        {/* Product facets sidebar (like an e‑commerce filter column) */}
        <div className="col-12 col-xl-3">
          <div className="card shadow-sm h-100">
            <div className="card-body">
              <div className="d-flex align-items-center justify-content-between mb-3">
                <h6 className="mb-0">Product Filters</h6>
                <button
                  className="btn btn-link btn-sm"
                  onClick={clearProductFilters}
                  disabled={Object.keys(productFilters).length === 0}
                >
                  Clear
                </button>
              </div>
              <div className="accordion" id="productFacetAccordion">
                {(productFacets.category || []).length > 0 && (
                  <div className="accordion-item">
                    <h2 className="accordion-header" id="product-category-facet">
                      <button
                        className="accordion-button"
                        type="button"
                        data-bs-toggle="collapse"
                        data-bs-target="#product-category-panel"
                      >
                        Categories
                      </button>
                    </h2>
                    <div id="product-category-panel" className="accordion-collapse collapse show">
                      <div className="accordion-body p-0">
                        <ul className="list-group list-group-flush">
                          {(productFacets.category || []).map(({ value, count }) => {
                            const inputId = makeFacetInputId('prod-cat', value);
                            return (
                              <li
                                key={value || 'unknown'}
                                className="list-group-item d-flex justify-content-between align-items-center"
                              >
                                <div className="form-check">
                                  <input
                                    className="form-check-input"
                                    type="checkbox"
                                    id={inputId}
                                    checked={(productFilters.category || []).includes(value)}
                                    onChange={() => toggleProductFacetValue('category', value)}
                                  />
                                  <label className="form-check-label" htmlFor={inputId}>
                                    {value || <span className="text-muted">Uncategorized</span>}
                                  </label>
                                </div>
                                <span className="badge bg-light text-dark">{count}</span>
                              </li>
                            );
                          })}
                          {(productFacets.category || []).length === 0 && (
                            <li className="list-group-item text-muted small">No categories available</li>
                          )}
                        </ul>
                      </div>
                    </div>
                  </div>
                )}
                {(productFacets.manufacturerName || []).length > 0 && (
                  <div className="accordion-item">
                    <h2 className="accordion-header" id="product-manufacturer-facet">
                      <button
                        className="accordion-button collapsed"
                        type="button"
                        data-bs-toggle="collapse"
                        data-bs-target="#product-manufacturer-panel"
                      >
                        Manufacturers
                      </button>
                    </h2>
                    <div id="product-manufacturer-panel" className="accordion-collapse collapse show">
                      <div className="accordion-body p-0">
                        <ul className="list-group list-group-flush">
                          {(productFacets.manufacturerName || []).map(({ value, count }) => {
                            const inputId = makeFacetInputId('prod-mfr', value);
                            return (
                              <li
                                key={value || 'unknown'}
                                className="list-group-item d-flex justify-content-between align-items-center"
                              >
                                <div className="form-check">
                                  <input
                                    className="form-check-input"
                                    type="checkbox"
                                    id={inputId}
                                    checked={(productFilters.manufacturerName || []).includes(value)}
                                    onChange={() => toggleProductFacetValue('manufacturerName', value)}
                                  />
                                  <label className="form-check-label" htmlFor={inputId}>
                                    {value || <span className="text-muted">Unknown manufacturer</span>}
                                  </label>
                                </div>
                                <span className="badge bg-light text-dark">{count}</span>
                              </li>
                            );
                          })}
                          {(productFacets.manufacturerName || []).length === 0 && (
                            <li className="list-group-item text-muted small">No manufacturers available</li>
                          )}
                        </ul>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Product search header, controls, and results */}
        <div className="col-12 col-xl-9">
          <div className="card shadow-sm">
            <div className="card-body">
              <div className="d-flex flex-column flex-lg-row align-items-start align-items-lg-center justify-content-between gap-3">
                <div>
                  <h4 className="mb-1">Product Solr Search</h4>
                  <p className="mb-0 text-muted">
                    Search your products and part numbers from the dedicated Solr collection.
                  </p>
                </div>
                <div className="d-flex gap-2 flex-wrap">
                  <button
                    className="btn btn-light"
                    disabled={productImporting === 'full' || productImportActive}
                    onClick={() => triggerProductImport('full')}
                  >
                    {productImporting === 'full'
                      ? 'Starting Full Import...'
                      : productImportActive
                        ? 'Import Running'
                        : 'Full Import'}
                  </button>
                  <button
                    className="btn btn-outline-primary"
                    disabled={productImporting === 'delta' || productImportActive}
                    onClick={() => triggerProductImport('delta')}
                  >
                    {productImporting === 'delta'
                      ? 'Starting Delta Import...'
                      : productImportActive
                        ? 'Import Running'
                        : 'Delta Import'}
                  </button>
                </div>
              </div>
              <div className="mt-3 d-flex flex-wrap gap-3 small text-muted">
                <div>
                  Collection:{' '}
                  <span className="text-dark fw-semibold">
                    {productCollection || 'products_<account_id>'}
                  </span>
                </div>
                <div>
                  Docs (acct / total):{' '}
                  <span className="text-dark fw-semibold">
                    {prettyNumber(productDocCounts.account_docs || 0)} /{' '}
                    {prettyNumber(productDocCounts.total_docs || 0)}
                  </span>
                </div>
                <div>
                  Orphan Docs:{' '}
                  <span
                    className={
                      productDocCounts.orphan_docs ? 'text-warning fw-semibold' : 'text-dark'
                    }
                  >
                    {prettyNumber(productDocCounts.orphan_docs || 0)}
                  </span>
                </div>
                <div>
                  Status:{' '}
                  <span className="text-dark fw-semibold">
                    {productImportState?.status || 'idle'}
                  </span>
                </div>
              </div>
              {productImportActive && (
                <div className="mt-3">
                  <div className="d-flex justify-content-between small text-muted mb-1">
                    <span>Importing {productImportState?.mode} data</span>
                    <span>
                      {prettyNumber(productImportState?.processed || 0)} /{' '}
                      {prettyNumber(productImportState?.total || 0)}
                    </span>
                  </div>
                  <div className="progress" style={{ height: '6px' }}>
                    <div
                      className="progress-bar"
                      style={{
                        width: `${
                          productImportState?.total
                            ? Math.min(
                                100,
                                Math.round(
                                  ((productImportState?.processed || 0) /
                                    productImportState.total) *
                                    100,
                                ),
                              )
                            : 0
                        }%`,
                      }}
                    />
                  </div>
                </div>
              )}

              <form onSubmit={handleProductSearchSubmit} className="row g-3 align-items-end mt-4">
                <div className="col-md-5">
                  <label className="form-label text-muted text-uppercase small fw-semibold">
                    Product search
                  </label>
                  <input
                    className="form-control"
                    placeholder="e.g., condenser fan motor"
                    value={productQuery}
                    onChange={(e) => setProductQuery(e.target.value)}
                  />
                </div>
                <div className="col-md-4">
                  <label className="form-label text-muted text-uppercase small fw-semibold">
                    Part number
                  </label>
                  <input
                    className="form-control"
                    placeholder="Part number or partial"
                    value={productPartnumber}
                    onChange={(e) => setProductPartnumber(e.target.value)}
                  />
                </div>
                <div className="col-md-3 d-grid">
                  <button type="submit" className="btn btn-primary" disabled={productLoading}>
                    {productLoading ? 'Searching...' : 'Search Products'}
                  </button>
                </div>
              </form>

              <div className="mt-4 d-flex flex-wrap gap-3 align-items-center">
                <div className="d-flex align-items-center gap-2">
                  <span className="text-muted small">Active filter:</span>
                  <select
                    className="form-select form-select-sm"
                    style={{ width: '140px' }}
                    value={productFilters.active ?? ''}
                    onChange={(e) => setProductActiveFilter(e.target.value)}
                  >
                    <option value="">Any</option>
                    <option value="true">Active</option>
                    <option value="false">Inactive</option>
                  </select>
                </div>
              </div>

              <div className="mt-4">
                <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-3 text-muted small">
                  <div>
                    Showing {productResults.length} of {prettyNumber(productStats.numFound)} products
                    {' '}• Page {productStats.page}
                  </div>
                  <div className="d-flex gap-2 mt-2 mt-md-0">
                    <button
                      type="button"
                      className="btn btn-outline-secondary btn-sm"
                      disabled={productLoading || productStats.page <= 1}
                      onClick={() =>
                        runProductSearch({ page: Math.max(1, productStats.page - 1) })
                      }
                    >
                      Previous
                    </button>
                    <button
                      type="button"
                      className="btn btn-outline-secondary btn-sm"
                      disabled={
                        productLoading ||
                        productStats.page >=
                          Math.max(
                            1,
                            Math.ceil(
                              (productStats.numFound || 0) /
                                (productStats.page_size || PRODUCT_PAGE_SIZE),
                            ),
                          )
                      }
                      onClick={() => runProductSearch({ page: productStats.page + 1 })}
                    >
                      Next
                    </button>
                  </div>
                </div>
                {productLoading && (
                  <div className="text-center py-4">
                    <div className="spinner-border text-primary mb-2" role="status" />
                    <div className="text-muted">Loading product results...</div>
                  </div>
                )}
                {!productLoading && productResults.length === 0 && (
                  <div className="text-center py-4 text-muted">
                    No products found yet. Try adjusting your query or filters.
                  </div>
                )}
                {!productLoading && productResults.length > 0 && (
                  <div className="row g-3">
                    {productResults.map((doc, index) => {
                      const docKey = doc.id || doc.productCode || doc.sku || `product-${index}`;
                      const hasAhriIds = Array.isArray(doc.ahriIds) && doc.ahriIds.length > 0;
                      const matchSummary = productMatchSummaries[docKey];
                      const matchError = productMatchErrors[docKey];

                      let matchSection = null;
                      if (hasAhriIds && (matchSummary || matchError)) {
                        const matches = matchSummary?.matches || [];
                        const filterText = (productMatchFilters[docKey] || '').trim().toLowerCase();
                        const facetFields = Array.isArray(matchSummary?.facet_fields)
                          ? matchSummary.facet_fields
                          : [];
                        const facetData = matchSummary?.facets || {};
                        const activeFacetFilters = productMatchFacetFilters[docKey] || {};

                        const byText = (m) => {
                          if (!filterText) return true;
                          const mapping = m.product_mapping || {};
                          const attrs = m.attributes || m.row_json || {};
                          const haystack =
                            JSON.stringify(mapping || {}, null, 0) +
                            ' ' +
                            JSON.stringify(attrs || {}, null, 0);
                          return haystack.toLowerCase().includes(filterText);
                        };

                        const byFacets = (m) => {
                          const attrs = m.attributes || m.row_json || {};
                          const attrObj = attrs && typeof attrs === 'object' ? attrs : {};
                          return Object.entries(activeFacetFilters).every(([field, selected]) => {
                            const sel = String(selected || '').trim();
                            if (!sel) return true;
                            const raw = attrObj[field];
                            const vals = Array.isArray(raw) ? raw : raw != null ? [raw] : [];
                            const normalized = vals.map((v) => String(v || '').trim());
                            return normalized.includes(sel);
                          });
                        };

                        const filteredMatches = matches.filter((m) => byText(m) && byFacets(m));

                        const handleFacetSelect = (field, value) => {
                          const selected = value || '';
                          setProductMatchFacetFilters((prev) => {
                            const nextForDoc = { ...(prev[docKey] || {}) };
                            if (!selected) {
                              delete nextForDoc[field];
                            } else {
                              nextForDoc[field] = selected;
                            }
                            return { ...prev, [docKey]: nextForDoc };
                          });
                        };

                        matchSection = (
                          <div className="mt-3 border-top pt-2">
                            <div className="d-flex justify-content-between align-items-center mb-1">
                              <span className="small text-muted text-uppercase">
                                AHRI Match Summary
                              </span>
                              {Array.isArray(doc.ahriIds) && doc.ahriIds.length > 0 && (
                                <span className="badge bg-light text-dark small">
                                  AHRI IDs: {doc.ahriIds.length}
                                </span>
                              )}
                            </div>
                            {matchError && (
                              <div className="alert alert-warning py-1 px-2 small mb-2">
                                {matchError}
                              </div>
                            )}
                            {!productMatchLoading[docKey] && filteredMatches.length === 0 && (
                              <>
                                <div className="small text-muted mb-1">
                                  Complete AHRI rows: {matchSummary?.total_complete_ahri}
                                </div>
                                <div className="small text-muted">
                                  No matches found for the current AHRI filter.
                                </div>
                              </>
                            )}
                            {filteredMatches.length > 0 && (
                              <>
                                <div className="mb-2">
                                  <div className="small text-muted mb-1">
                                    Complete AHRI rows: {matchSummary?.total_complete_ahri}
                                  </div>
                                  {facetFields.length > 0 && (
                                    <div className="mb-2">
                                      <div className="small text-muted mb-1">AHRI Match Facets</div>
                                      <div className="row g-2">
                                        {facetFields.map((field) => {
                                          const values = facetData[field] || [];
                                          if (!values.length) return null;
                                          const selected = activeFacetFilters[field] || '';
                                          return (
                                            <div key={field} className="col-12 col-sm-6 col-md-4">
                                              <label className="form-label form-label-sm text-muted small mb-1">
                                                {humanizeFacetLabel(field)}
                                              </label>
                                              <select
                                                className="form-select form-select-sm"
                                                value={selected}
                                                onChange={(e) => handleFacetSelect(field, e.target.value)}
                                              >
                                                <option value="">Any</option>
                                                {values.map(({ value }) => (
                                                  <option key={String(value)} value={String(value)}>
                                                    {String(value || '—')}
                                                  </option>
                                                ))}
                                              </select>
                                            </div>
                                          );
                                        })}
                                      </div>
                                    </div>
                                  )}
                                  <div className="d-flex flex-column flex-md-row gap-2 align-items-stretch align-items-md-center">
                                    <div className="flex-grow-1">
                                      <input
                                        type="text"
                                        className="form-control form-control-sm"
                                        placeholder="Filter AHRI matches by attribute or mapped code…"
                                        value={productMatchFilters[docKey] || ''}
                                        onChange={(e) =>
                                          setProductMatchFilters((prev) => ({
                                            ...prev,
                                            [docKey]: e.target.value,
                                          }))
                                        }
                                      />
                                    </div>
                                  </div>
                                </div>
                                <div style={{ maxHeight: 360, overflowY: 'auto' }}>
                                  <div className="row g-2">
                                    {filteredMatches.map((m, idx) => {
                                      const mapping = m.product_mapping || {};
                                      const attrs = m.attributes || m.row_json || {};
                                      const allCodesSet = new Set();
                                      Object.entries(mapping).forEach(([, rawVal]) => {
                                        const vals = Array.isArray(rawVal)
                                          ? rawVal
                                          : String(rawVal || '')
                                              .split(',')
                                              .map((s) => s.trim());
                                        vals
                                          .map((v) => String(v || '').trim())
                                          .filter(Boolean)
                                          .forEach((v) => allCodesSet.add(v));
                                      });
                                      // Exclude the current product from mapped products
                                      if (doc.productCode) {
                                        allCodesSet.delete(String(doc.productCode).trim());
                                      }
                                      const allCodes = Array.from(allCodesSet);
                                      return (
                                        <div key={`${m.ahri_id || 'ahri'}-${idx}`} className="col-12">
                                          <div className="border rounded-3 p-2 mb-2 bg-light-subtle small h-100">
                                            <div className="d-flex flex-column flex-sm-row justify-content-between align-items-sm-center mb-1 gap-1">
                                              <div className="small text-break">
                                                <span className="text-muted">AHRI ID:</span>{' '}
                                                <code>{m.ahri_id || 'n/a'}</code>
                                              </div>
                                              <div className="small text-muted d-flex flex-wrap gap-1">
                                                {m.program_name && (
                                                  <span className="me-2">{m.program_name}</span>
                                                )}
                                                {m.model_status && (
                                                  <span className="badge bg-primary-subtle text-primary">
                                                    {m.model_status}
                                                  </span>
                                                )}
                                              </div>
                                            </div>
                                            {allCodes.length > 0 && (
                                              <div className="mt-1">
                                                <div className="small text-muted mb-1">Mapped products</div>
                                                {productMatchProducts[docKey]?.loading && (
                                                  <div className="small text-muted d-flex align-items-center gap-1">
                                                    <span className="spinner-border spinner-border-sm text-primary" role="status" />
                                                    <span>Loading mapped products…</span>
                                                  </div>
                                                )}
                                                {productMatchProducts[docKey]?.error &&
                                                  !productMatchProducts[docKey]?.loading && (
                                                    <div className="alert alert-warning py-1 px-2 small mb-2">
                                                      {productMatchProducts[docKey].error}
                                                    </div>
                                                )}
                                                {!productMatchProducts[docKey]?.loading && (
                                                  <div className="row g-2">
                                                    {allCodes.map((code) => {
                                                      const key = String(code || '').trim().toLowerCase();
                                                      const p = productMatchProducts[docKey]?.byCode?.[key];
                                                      if (!p) {
                                                        return (
                                                          <div key={code} className="col-12 col-md-6">
                                                            <div className="border rounded-3 h-100 p-2 d-flex flex-column small text-muted">
                                                              <div className="fw-semibold text-truncate">
                                                                <code>{code}</code>
                                                              </div>
                                                              <div className="mt-auto small">
                                                                No product details found
                                                              </div>
                                                            </div>
                                                          </div>
                                                        );
                                                      }
                                                      return (
                                                        <div
                                                          key={p.id || p.productCode || code}
                                                          className="col-12 col-md-6"
                                                        >
                                                          <div className="border rounded-3 h-100 p-2 d-flex flex-column">
                                                            <div className="d-flex align-items-center gap-2 mb-2">
                                                              <div
                                                                className="ratio ratio-1x1 rounded border bg-light flex-shrink-0"
                                                                style={{ width: 48, overflow: 'hidden' }}
                                                              >
                                                                {buildProductImageUrl(p.imageUrl, productImageBaseUrl) ? (
                                                                  <img
                                                                    src={buildProductImageUrl(p.imageUrl, productImageBaseUrl)}
                                                                    alt={p.name || p.productCode || 'Product image'}
                                                                    style={{ objectFit: 'cover' }}
                                                                  />
                                                                ) : (
                                                                  <div className="d-flex align-items-center justify-content-center text-muted small">
                                                                    No image
                                                                  </div>
                                                                )}
                                                              </div>
                                                              <div className="flex-grow-1">
                                                                <div className="fw-semibold small text-truncate">
                                                                  {p.name || p.productCode || 'Mapped product'}
                                                                </div>
                                                                <div className="text-muted small text-truncate">
                                                                  Code:{' '}
                                                                  <code>{p.productCode || code}</code>
                                                                </div>
                                                              </div>
                                                            </div>
                                                            {p.manufacturerName && (
                                                              <div className="text-muted small mb-1">
                                                                Manufacturer: {p.manufacturerName}
                                                              </div>
                                                            )}
                                                            {p.manufacturerPartNumber && (
                                                              <div className="text-muted small mb-1">
                                                                MPN:{' '}
                                                                <code>{p.manufacturerPartNumber}</code>
                                                              </div>
                                                            )}
                                                            {p.category && (
                                                              <div className="mt-auto small">
                                                                <span className="badge bg-light text-dark">
                                                                  {p.category}
                                                                </span>
                                                              </div>
                                                            )}
                                                          </div>
                                                        </div>
                                                      );
                                                    })}
                                                  </div>
                                                )}
                                              </div>
                                            )}
                                            {attrs && Object.keys(attrs).length > 0 && (
                                              <details className="mt-2 small">
                                                <summary className="text-primary">
                                                  View AHRI attributes
                                                </summary>
                                                <pre
                                                  className="bg-light rounded p-2 mt-1 text-break"
                                                  style={{
                                                    maxHeight: 200,
                                                    overflow: 'auto',
                                                    wordBreak: 'break-word',
                                                  }}
                                                >
                                                  {JSON.stringify(attrs, null, 2)}
                                                </pre>
                                              </details>
                                            )}
                                          </div>
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>
                              </>
                            )}
                          </div>
                        );
                      }

                      return (
                        <div className="col-12 col-lg-6" key={docKey}>
                          <div className="card h-100">
                            <div className="card-body">
                              <div className="d-flex gap-3">
                                {buildProductImageUrl(doc.imageUrl, productImageBaseUrl) && (
                                  <img
                                    src={buildProductImageUrl(doc.imageUrl, productImageBaseUrl)}
                                    alt={doc.name || doc.productCode}
                                    style={{ width: '72px', height: '72px', objectFit: 'cover' }}
                                    className="rounded border"
                                  />
                                )}
                                <div className="flex-grow-1">
                                  <div className="d-flex justify-content-between align-items-start">
                                    <div>
                                      <h5 className="mb-1">{doc.name || doc.productCode}</h5>
                                      <div className="text-muted small">
                                        Code: <code>{doc.productCode}</code>
                                      </div>
                                    </div>
                                    {doc.active && (
                                      <span className="badge bg-success-subtle text-success">
                                        Active
                                      </span>
                                    )}
                                  </div>
                                  {doc.category && (
                                    <div className="small text-muted mt-1">
                                      Category: {doc.category}
                                    </div>
                                  )}
                                </div>
                              </div>
                              {doc.description && (
                                <p className="text-muted small mt-3 mb-0">{doc.description}</p>
                              )}
                              <div className="d-flex flex-wrap gap-2 mt-2">
                                {doc.manufacturerName && (
                                  <span className="badge bg-light text-dark">
                                    MFR: {doc.manufacturerName}
                                  </span>
                                )}
                                {doc.manufacturerPartNumber && (
                                  <span className="badge bg-light text-dark">
                                    MFR Part #: {doc.manufacturerPartNumber}
                                  </span>
                                )}
                              </div>
                              <div className="mt-3 d-flex flex-wrap gap-2">
                                <button
                                  type="button"
                                  className="btn btn-sm btn-outline-secondary"
                                  onClick={() => toggleProductDetail(docKey)}
                                >
                                  {expandedProductDetails[docKey] ? 'Hide Info' : 'More Info'}
                                </button>
                                {hasAhriIds && (
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-outline-primary"
                                    onClick={() =>
                                      loadAhriMatchForProduct(docKey, doc.productCode)
                                    }
                                    disabled={productMatchLoading[docKey]}
                                  >
                                    {productMatchLoading[docKey]
                                      ? 'Loading AHRI Match…'
                                      : 'AHRI Match'}
                                  </button>
                                )}
                              </div>
                              {expandedProductDetails[docKey] && (
                                <div className="mt-2">{renderDetailTable(doc)}</div>
                              )}
                              {matchSection}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      )}

      {activeTab === 'builder' && (
        <div className="row g-4">
          <div className="col-12">
            <div className="bg-primary text-white rounded-4 p-4 mb-3">
              <div className="row g-3 align-items-center">
                <div className="col-12 col-lg-8">
                  <h2 className="mb-2">AC System Builder</h2>
                  <p className="mb-0 text-white-50">
                    Configure complete, AHRI-certified systems by searching matched combinations and
                    viewing the exact indoor, outdoor, and accessory products that make up each system.
                  </p>
                </div>
                <div className="col-12 col-lg-4">
                  {selectedSystemKey && (
                    <div className="card border-0 shadow-sm">
                      <div className="card-body py-3 px-3">
                        <div className="text-uppercase text-muted small mb-1">Selected System</div>
                        <div className="small">
                          <div className="fw-semibold text-truncate">{selectedSystemKey}</div>
                          <div className="text-white-50">
                            Review the components below and confirm this system in your quote or cart
                            workflow.
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="col-12 col-xl-3">
            <div className="card shadow-sm h-100">
              <div className="card-body">
                <div className="d-flex align-items-center justify-content-between mb-3">
                  <h6 className="mb-0">System Filters</h6>
                  <button
                    type="button"
                    className="btn btn-link btn-sm"
                    onClick={clearBuilderFilters}
                    disabled={Object.keys(builderFilters || {}).length <= 1}
                  >
                    Clear
                  </button>
                </div>
                <div className="accordion" id="builderFacetAccordion">
                  {orderedBuilderFacetDefs.map((facet, idx) => {
                    const field = facet.field;
                    const values = builderFacets[field] || [];
                    const label =
                      facet.label || staticFacetLabels[field] || humanizeFacetLabel(field);
                    const help = builderFacetHelpText[field] || '';
                    if (!values.length) return null;
                    return (
                      <div className="accordion-item" key={field}>
                        <h2 className="accordion-header" id={`builder-facet-${field}`}>
                          <button
                            className={`accordion-button ${idx === 0 ? '' : 'collapsed'}`}
                            type="button"
                            data-bs-toggle="collapse"
                            data-bs-target={`#builder-facet-panel-${field}`}
                          >
                            <span>
                              {label}
                              {help && (
                                <span
                                  className="ms-1 text-muted"
                                  style={{ cursor: 'help' }}
                                  title={help}
                                >
                                  ?
                                </span>
                              )}
                            </span>
                          </button>
                        </h2>
                        <div
                          id={`builder-facet-panel-${field}`}
                          className={`accordion-collapse collapse ${idx === 0 ? 'show' : ''}`}
                          data-bs-parent="#builderFacetAccordion"
                        >
                          <div className="accordion-body p-0">
                            <ul className="list-group list-group-flush">
                              {values.map(({ value, count }) => {
                                const inputId = makeFacetInputId(`builder-${field}`, value);
                                return (
                                  <li
                                    key={value || 'unknown'}
                                    className="list-group-item d-flex justify-content-between align-items-center"
                                  >
                                    <div className="form-check">
                                      <input
                                        className="form-check-input"
                                        type="checkbox"
                                        id={inputId}
                                        checked={(builderFilters[field] || []).includes(value)}
                                        onChange={() => toggleBuilderFacetValue(field, value)}
                                      />
                                      <label className="form-check-label" htmlFor={inputId}>
                                        {value || <span className="text-muted">—</span>}
                                      </label>
                                    </div>
                                    <span className="badge bg-light text-dark">{count}</span>
                                  </li>
                                );
                              })}
                            </ul>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  {orderedBuilderFacetDefs.length === 0 && (
                    <div className="text-muted small">
                      No facet data available yet. Run a search to see available filters.
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="col-12 col-xl-9">
            <div className="card shadow-sm mb-3">
              <div className="card-body">
                <form onSubmit={handleBuilderSearchSubmit} className="row g-3 align-items-end">
                  <div className="col-md-9">
                    <label className="form-label text-muted text-uppercase small fw-semibold">
                      Search systems
                    </label>
                    <input
                      className="form-control form-control-lg"
                      placeholder="Search by AHRI ID, brand, SEER, tonnage, or series"
                      value={builderQuery}
                      onChange={(e) => setBuilderQuery(e.target.value)}
                    />
                  </div>
                  <div className="col-md-3 d-grid">
                    <button
                      type="submit"
                      className="btn btn-primary btn-lg"
                      disabled={builderLoading}
                    >
                      {builderLoading ? 'Searching systems…' : 'Find Systems'}
                    </button>
                  </div>
                </form>
                <div className="mt-3 text-muted small d-flex flex-wrap gap-2">
                  <div>
                    Showing {builderResults.length} of {prettyNumber(builderStats.numFound)}{' '}
                    eligible systems • Page {builderStats.page}
                  </div>
                  <div className="d-flex gap-2 ms-auto">
                    <button
                      type="button"
                      className="btn btn-outline-secondary btn-sm"
                      disabled={builderLoading || builderStats.page <= 1}
                      onClick={() =>
                        runBuilderSearch({ page: Math.max(1, builderStats.page - 1) })
                      }
                    >
                      Previous
                    </button>
                    <button
                      type="button"
                      className="btn btn-outline-secondary btn-sm"
                      disabled={
                        builderLoading ||
                        builderStats.page >=
                          Math.max(
                            1,
                            Math.ceil(
                              (builderStats.numFound || 0) /
                                (builderStats.page_size || builderResults.length || 12),
                            ),
                          )
                      }
                      onClick={() => runBuilderSearch({ page: builderStats.page + 1 })}
                    >
                      Next
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {builderLoading && (
              <div className="card shadow-sm">
                <div className="card-body text-center py-5">
                  <div className="spinner-border text-primary mb-3" role="status" />
                  <div className="text-muted">Loading matched systems…</div>
                </div>
              </div>
            )}
            {!builderLoading && builderResults.length === 0 && (
              <div className="card shadow-sm">
                <div className="card-body text-center py-5">
                  <h5>No systems yet</h5>
                  <p className="text-muted mb-0">
                    Run a search above to see AHRI-complete systems that match your requirements.
                  </p>
                </div>
              </div>
            )}
            {!builderLoading && builderResults.length > 0 && (
              <div className="row g-3">
                {builderResults.map((doc, index) => {
                  const docKey =
                    doc.id ||
                    doc.referenceId ||
                    doc.ahriId ||
                    doc.ahriNumber ||
                    doc.productCode ||
                    `builder-${index}`;
                  let payloadObj = {};
                  if (doc.payload_json) {
                    try {
                      payloadObj = JSON.parse(doc.payload_json);
                    } catch {
                      payloadObj = {};
                    }
                  }
                  const programName =
                    payloadObj.programName ||
                    payloadObj.program ||
                    (Array.isArray(doc.rows_programname) ? doc.rows_programname[0] : undefined) ||
                    'Program';
                  const outdoorBrandName =
                    payloadObj.outdoorBrandName ||
                    (Array.isArray(doc.rows_outdoorbrandname)
                      ? doc.rows_outdoorbrandname[0]
                      : undefined);
                  const indoorBrandName =
                    payloadObj.indoorBrandName ||
                    (Array.isArray(doc.rows_indoorbrandname)
                      ? doc.rows_indoorbrandname[0]
                      : undefined);
                  const seriesName = (() => {
                    if (payloadObj.seriesName) return payloadObj.seriesName;
                    if (payloadObj.series) return payloadObj.series;
                    const seriesKeys = Object.keys(doc || {}).filter((k) =>
                      k.startsWith('rows_series'),
                    );
                    for (const key of seriesKeys) {
                      const v = doc[key];
                      if (Array.isArray(v) && v.length > 0 && v[0]) return v[0];
                      if (typeof v === 'string' && v.trim()) return v;
                    }
                    return undefined;
                  })();
                  const region =
                    payloadObj.region ||
                    (Array.isArray(doc.rows_region) ? doc.rows_region[0] : undefined);
                  const seer =
                    payloadObj.seerM ??
                    payloadObj.seerM1 ??
                    payloadObj.seer ??
                    null;
                  const eer95 =
                    payloadObj.eer95FM ??
                    payloadObj.eer95FM1 ??
                    payloadObj.eer95 ??
                    null;
                  let ahriId =
                    payloadObj.ahri_id ||
                    payloadObj.ahriId ||
                    payloadObj.referenceId ||
                    payloadObj.AHRIReferenceNumber ||
                    null;
                  if (!ahriId && doc.id) {
                    const idParts = String(doc.id).split('_', 2);
                    ahriId = idParts.length === 2 ? idParts[1] : doc.id;
                  }
                  const mappedProductCodes = Array.from(
                    new Set(
                      Object.entries(doc)
                        .filter(([key]) => key.startsWith('prodmap_'))
                        .flatMap(([, value]) => (Array.isArray(value) ? value : [value]))
                        .map((v) => String(v || '').trim())
                        .filter(Boolean),
                    ),
                  );

                  const isSelected = selectedSystemKey === docKey;
                  const attributeEntries = Object.entries(payloadObj || {}).sort(
                    ([aKey, aVal], [bKey, bVal]) => {
                      const hasA =
                        aVal !== null &&
                        aVal !== undefined &&
                        !(typeof aVal === 'string' && aVal.trim() === '') &&
                        !(Array.isArray(aVal) && aVal.length === 0) &&
                        !(typeof aVal === 'object' &&
                          !Array.isArray(aVal) &&
                          aVal !== null &&
                          Object.keys(aVal).length === 0);
                      const hasB =
                        bVal !== null &&
                        bVal !== undefined &&
                        !(typeof bVal === 'string' && bVal.trim() === '') &&
                        !(Array.isArray(bVal) && bVal.length === 0) &&
                        !(typeof bVal === 'object' &&
                          !Array.isArray(bVal) &&
                          bVal !== null &&
                          Object.keys(bVal).length === 0);
                      if (hasA !== hasB) {
                        return hasA ? -1 : 1;
                      }
                      return aKey.localeCompare(bKey);
                    },
                  );

                  return (
                    <div className="col-12" key={docKey}>
                      <div
                        className={`card h-100 ${isSelected ? 'border-primary border-2' : ''}`}
                      >
                        <div className="card-body d-flex flex-column">
                          <div className="d-flex justify-content-between align-items-start mb-2">
                            <div>
                              <div className="text-uppercase text-muted small mb-1">
                                {programName}
                              </div>
                              <div className="fw-semibold">
                                AHRI ID: <code>{ahriId || 'n/a'}</code>
                              </div>
                              {seriesName && (
                                <div className="small text-muted mt-1">
                                  Series: <span className="fw-semibold">{seriesName}</span>
                                </div>
                              )}
                            </div>
                            <div className="text-end small">
                              {outdoorBrandName && (
                                <div className="badge bg-secondary-subtle text-secondary mb-1">
                                  Outdoor: {outdoorBrandName}
                                </div>
                              )}
                              {indoorBrandName && (
                                <div className="badge bg-info-subtle text-info d-block">
                                  Indoor: {indoorBrandName}
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="row g-3 mb-3">
                            <div className="col-6 col-md-3">
                              <div className="text-muted small">SEER</div>
                              <div className="fw-semibold">{seer ?? '—'}</div>
                            </div>
                            <div className="col-6 col-md-3">
                              <div className="text-muted small">EER95</div>
                              <div className="fw-semibold">{eer95 ?? '—'}</div>
                            </div>
                            <div className="col-12 col-md-6">
                              <div className="text-muted small">Region</div>
                              <div className="fw-semibold">{region || '—'}</div>
                            </div>
                          </div>
                          {mappedProductCodes.length > 0 && (
                            <div className="mt-1">
                              <div className="d-flex justify-content-between align-items-center mb-1">
                                <div className="small text-uppercase text-muted">
                                  System Components
                                </div>
                                <div className="small text-muted">
                                  {mappedProductCodes.length} mapped product
                                  {mappedProductCodes.length === 1 ? '' : 's'}
                                </div>
                              </div>
                              <MappedProducts
                                codes={mappedProductCodes}
                                headers={headers}
                                imageBaseUrl={productImageBaseUrl}
                              />
                            </div>
                          )}
                          {payloadObj && Object.keys(payloadObj).length > 0 && (
                            <div className="mt-3">
                              <div className="d-flex justify-content-between align-items-center mb-1">
                                <div className="small text-uppercase text-muted">
                                  System Attributes
                                </div>
                                <button
                                  type="button"
                                  className="btn btn-sm btn-outline-secondary"
                                  onClick={() =>
                                    setExpandedBuilderAttributes((prev) => ({
                                      ...prev,
                                      [docKey]: !prev[docKey],
                                    }))
                                  }
                                >
                                  {expandedBuilderAttributes[docKey]
                                    ? 'Hide Details'
                                    : 'Show Details'}
                                </button>
                              </div>
                              {expandedBuilderAttributes[docKey] && (
                                <div className="border rounded-3 bg-light-subtle p-2">
                                  <div className="row g-2">
                                    {attributeEntries.map(([key, value]) => (
                                      <div className="col-12 col-md-6" key={key}>
                                        <div className="bg-white rounded-3 h-100 p-2 shadow-sm-sm">
                                          <div className="text-uppercase text-muted small fw-semibold mb-1">
                                            {key}
                                          </div>
                                          <div
                                            className="small text-break"
                                            style={{ wordBreak: 'break-word', whiteSpace: 'pre-wrap' }}
                                          >
                                            {formatDetailValue(value)}
                                          </div>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          )}
                          <div className="mt-3 d-flex justify-content-between align-items-center">
                            <button
                              type="button"
                              className={`btn btn-sm ${
                                isSelected ? 'btn-primary' : 'btn-outline-primary'
                              }`}
                              onClick={() => setSelectedSystemKey(isSelected ? null : docKey)}
                            >
                              {isSelected ? 'Selected System' : 'Choose this System'}
                            </button>
                            <div className="small text-muted">
                              Use this combination when configuring quotes or installation packages.
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AhriSolr;


